import {useCallback, useEffect, useMemo, useState} from 'react';
import {Body2} from '@ui/atoms/typography';
import CommentIcon from '@ui/atoms/icons/comment-icon';
import {useFirebaseFetch} from '@hooks/query-hook';
import {useStoreContext} from '@store/store-context';
import {
  AddMentions,
  addCheckinCommentNotification,
  saveCheckinCommentThreadSubscriptions,
} from '@utils/firebase-request';
import {InputComment} from '@ui/layouts/users-goals/comments/input-comments';
import {CommentsBody} from '@ui/layouts/users-goals/comments/comments-body';
import {CheckinCommentNotificationContext} from '@pages/dashboard/notifications/notification.types';
import {activateNotification} from '@ui/molecules/notification/activate-notification';
import {RawDraftContentState} from 'draft-js';
import {
  buildThreadCommentDelivery,
  getEffectiveThreadSubscriberIds,
  toggleThreadSubscription,
} from './team-feed-comments.utils';

interface TeamFeedCommentsProps {
  submissionId: string;
  userId: string;
  mentionableUserIds: string[];
  defaultOpen?: boolean;
  notificationContext?: CheckinCommentNotificationContext;
}

interface TeamFeedCommentThreadProps {
  submissionId: string;
  commentsPath: string;
  mentionsPath: string;
  comments: Record<string, unknown>;
  defaultSubscribers: string[];
  mentionableUserIds: string[];
  notificationContext?: CheckinCommentNotificationContext;
  subscriberIds: string[];
  isSubscribed: boolean;
  isUpdatingSubscription: boolean;
  onCommentCreated: (input: CommentCreatedInput) => Promise<void>;
  onToggleSubscription: () => Promise<void>;
}

interface CommentCreatedInput {
  mentionedUserIds: string[];
  commentId?: string | null;
  rawComment?: RawDraftContentState;
}

const TeamFeedCommentThread = ({
  submissionId,
  commentsPath,
  mentionsPath,
  comments,
  defaultSubscribers,
  mentionableUserIds,
  notificationContext,
  subscriberIds,
  isSubscribed,
  isUpdatingSubscription,
  onCommentCreated,
  onToggleSubscription,
}: TeamFeedCommentThreadProps) => {
  const [editComments, setEditComments] = useState<any>();
  const [localEntities, setLocalEntities] = useState<
    Record<string, RawDraftContentState>
  >({});
  const {data: entities} = useFirebaseFetch('commentsEntities');
  const commentCount = Object.keys(comments).length;
  const resolvedEntities = useMemo(
    () => ({...localEntities, ...(entities || {})}),
    [entities, localEntities],
  );

  const handleEditComments = (comment: any) => {
    if (!comment) {
      setEditComments(comment);
      return;
    }
    const commentEntities = resolvedEntities?.[comment[0]];
    setEditComments([
      comment[0],
      {...comment[1], entities: commentEntities || comment[1]?.entities},
    ]);
  };

  const handleCommentCreated = useCallback(
    async (input: CommentCreatedInput) => {
      if (input.commentId && input.rawComment) {
        setLocalEntities((current) => ({
          ...current,
          [input.commentId as string]: input.rawComment as RawDraftContentState,
        }));
      }

      await onCommentCreated(input);
    },
    [onCommentCreated],
  );

  return (
    <div
      id={`team-feed-comments-${submissionId}`}
      className="border-t border-borderLight bg-backgroundDark px-4 pb-4 pt-2">
      <div className="pt-3">
        <InputComment
          pathId={submissionId}
          defaultSubscribers={defaultSubscribers}
          allowedMentionedUsers={mentionableUserIds}
          mentionsPath={mentionsPath}
          path={commentsPath}
          allGoalSubscribers={subscriberIds}
          editComments={editComments}
          handleEditComments={handleEditComments}
          placeholder="Add a comment..."
          submitLabel="Post"
          layoutVariant="teamFeed"
          enableRichText
          notificationContext={notificationContext}
          onCommentCreated={handleCommentCreated}
        />
        <div className="flex items-center justify-between gap-4 pt-2">
          <Body2 kind="textMedium">@ - mention a team member.</Body2>
          <button
            type="button"
            disabled={isUpdatingSubscription}
            onClick={() => void onToggleSubscription()}
            className="shrink-0 text-sm font-medium text-purple300 disabled:cursor-not-allowed disabled:text-textMuted">
            {isUpdatingSubscription
              ? 'Updating…'
              : isSubscribed
              ? 'Mute thread'
              : 'Follow thread'}
          </button>
        </div>
      </div>
      {commentCount > 0 && (
        <div className="pt-3">
          <CommentsBody
            comments={comments}
            goalId={submissionId}
            path={commentsPath}
            mentions={[]}
            richTextEntities={resolvedEntities}
            editComments={editComments}
            handleEditComments={handleEditComments}
          />
        </div>
      )}
    </div>
  );
};

export const TeamFeedComments = ({
  submissionId,
  userId,
  mentionableUserIds,
  defaultOpen = false,
  notificationContext,
}: TeamFeedCommentsProps) => {
  const {
    authStore: {auth},
  } = useStoreContext();
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdatingSubscription, setIsUpdatingSubscription] = useState(false);
  const commentsPath = `checkins/comments/${submissionId}/${userId}`;
  const mentionsPath = `checkins/comments/${submissionId}`;
  const {data: commentsData = {}, isLoading} = useFirebaseFetch(
    `${commentsPath}/comments`,
  );
  const {data: mentionData} = useFirebaseFetch(
    `users/${auth.user.id}/mentions/checkins/comments/${submissionId}`,
  );
  const {data: subscriberData = []} = useFirebaseFetch(
    `${commentsPath}/subscribers`,
  );
  const {data: mutedSubscriberData = []} = useFirebaseFetch(
    `${commentsPath}/mutedSubscribers`,
  );
  const comments = useMemo(() => commentsData || {}, [commentsData]);
  const commentCount = Object.keys(comments).length;
  const hasUnread = Boolean(
    mentionData?.unreadComment || mentionData?.mentionedBy?.length,
  );
  const historicalCommenterIds = useMemo(
    () =>
      Object.values(comments)
        .map((comment: any) => comment?.commentBy)
        .filter(Boolean),
    [comments],
  );
  const subscriberIds = useMemo(
    () =>
      getEffectiveThreadSubscriberIds({
        subscriberIds: subscriberData,
        mutedSubscriberIds: mutedSubscriberData,
        historicalCommenterIds,
        participantId: userId,
      }),
    [historicalCommenterIds, mutedSubscriberData, subscriberData, userId],
  );
  const isSubscribed = subscriberIds.includes(auth.user.id);

  const handleCommentCreated = useCallback(
    async ({mentionedUserIds}: CommentCreatedInput) => {
      const delivery = buildThreadCommentDelivery({
        subscriberIds: subscriberData,
        mutedSubscriberIds: mutedSubscriberData,
        historicalCommenterIds,
        participantId: userId,
        actorId: auth.user.id,
        mentionedUserIds,
      });
      const createdAt = Date.now();
      const actorName =
        auth.user.firstName && auth.user.lastName
          ? `${auth.user.firstName} ${auth.user.lastName}`
          : auth.user.email || 'A teammate';

      await saveCheckinCommentThreadSubscriptions(
        commentsPath,
        delivery.subscriberIds,
        delivery.mutedSubscriberIds,
      );
      await Promise.all(
        delivery.recipientIds.map((recipientId) => {
          const href =
            recipientId === notificationContext?.participantId
              ? notificationContext?.ownerHref
              : notificationContext?.defaultHref ||
                notificationContext?.ownerHref;
          const notification = notificationContext
            ? {
                ...Object.fromEntries(
                  Object.entries(notificationContext).filter(
                    ([, value]) => value !== undefined,
                  ),
                ),
                actorId: auth.user.id,
                actorName,
                createdAt,
                ...(href ? {href} : {}),
              }
            : undefined;

          return addCheckinCommentNotification({
            recipientId,
            submissionId,
            actorId: auth.user.id,
            isMention: delivery.mentionedRecipientIds.includes(recipientId),
            notification,
          });
        }),
      );
    },
    [
      auth.user.email,
      auth.user.firstName,
      auth.user.id,
      auth.user.lastName,
      commentsPath,
      historicalCommenterIds,
      mutedSubscriberData,
      notificationContext,
      subscriberData,
      submissionId,
      userId,
    ],
  );

  const handleToggleSubscription = useCallback(async () => {
    const nextState = toggleThreadSubscription({
      subscriberIds: subscriberData,
      mutedSubscriberIds: mutedSubscriberData,
      historicalCommenterIds,
      participantId: userId,
      userId: auth.user.id,
    });

    setIsUpdatingSubscription(true);
    try {
      await saveCheckinCommentThreadSubscriptions(
        commentsPath,
        nextState.subscriberIds,
        nextState.mutedSubscriberIds,
      );
      activateNotification({
        title: nextState.isSubscribed ? 'Thread followed' : 'Thread muted',
        content: nextState.isSubscribed
          ? 'You will receive notifications for new replies.'
          : 'You will no longer receive notifications for new replies.',
        kind: 'success',
      });
    } catch {
      activateNotification({
        title: 'Unable to update thread',
        content: 'Please check your connection and try again.',
        kind: 'error',
      });
    } finally {
      setIsUpdatingSubscription(false);
    }
  }, [
    auth.user.id,
    commentsPath,
    historicalCommenterIds,
    mutedSubscriberData,
    subscriberData,
    userId,
  ]);

  useEffect(() => {
    if (defaultOpen) setIsOpen(true);
  }, [defaultOpen]);

  useEffect(() => {
    if (!isOpen || !hasUnread) return;
    AddMentions(
      auth.user.id,
      submissionId,
      {unreadComment: 0, mentionedBy: []},
      'checkins/comments',
    );
  }, [auth.user.id, hasUnread, isOpen, submissionId]);

  const countLabel = `${commentCount} ${
    commentCount === 1 ? 'comment' : 'comments'
  }`;

  return (
    <div className="border-t border-borderLight">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={`team-feed-comments-${submissionId}`}
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full items-center gap-3 px-4 py-4 text-left hover:bg-backgroundLight focus:outline-none focus-visible:ring-2 focus-visible:ring-purple300">
        <CommentIcon />
        <Body2 kind="textDark" weight="semibold">
          {isLoading ? 'Loading comments…' : countLabel}
        </Body2>
        {hasUnread && (
          <span
            aria-label="Unread comments"
            className="h-2 w-2 rounded-full bg-red400"
          />
        )}
        <Body2 kind="purple300" weight="semibold" className="ml-auto">
          {isOpen ? 'Close replies' : 'Reply'}
        </Body2>
      </button>

      {isOpen && (
        <TeamFeedCommentThread
          submissionId={submissionId}
          commentsPath={commentsPath}
          mentionsPath={mentionsPath}
          comments={comments}
          defaultSubscribers={[auth.user.id, userId].filter(Boolean)}
          mentionableUserIds={mentionableUserIds}
          notificationContext={notificationContext}
          subscriberIds={subscriberIds}
          isSubscribed={isSubscribed}
          isUpdatingSubscription={isUpdatingSubscription}
          onCommentCreated={handleCommentCreated}
          onToggleSubscription={handleToggleSubscription}
        />
      )}
    </div>
  );
};
